/**
 * API Client for Bullet-Mapping configuration (PR2 #10).
 *
 * Wraps the v1 endpoints introduced in commit 535b8b41:
 *   - GET /api/v1/integrations/{id}/jtl/customfields  → list JTL custom fields
 *   - GET /api/v1/integrations/{id}/bullet-mapping    → read mapping (all channels)
 *   - PUT /api/v1/integrations/{id}/bullet-mapping    → upsert per-channel mapping
 *
 * The mapping is keyed by sales_channel_id; per channel up to 5 slots
 * (bullet_1..bullet_5) point to a JTL custom-field id.
 */
import { apiClient } from '@/lib/api-client'

export interface JtlCustomFieldDef {
  /** JTL custom-field id (numeric, used as mapping target) */
  id: number
  /** Human-readable name shown in the dropdown */
  name: string
}

export interface BulletMapping {
  bullet_1?: number | null
  bullet_2?: number | null
  bullet_3?: number | null
  bullet_4?: number | null
  bullet_5?: number | null
  /** Optional: maps Backend-Keywords to a JTL custom-field (PR2 #6). */
  keywords?: number | null
}

export type ChannelBulletMappings = Record<string, BulletMapping>

export interface CustomFieldDefsResponse {
  definitions: JtlCustomFieldDef[]
}

export interface BulletMappingsResponse {
  bullet_mapping: ChannelBulletMappings
}

export interface SaveBulletMappingResponse {
  saved: boolean
  mapping: BulletMapping
}

/**
 * List JTL custom-field definitions for an integration (used as dropdown options).
 */
export async function getCustomFieldDefs(
  integrationId: string,
): Promise<CustomFieldDefsResponse> {
  const resp = await apiClient.get<CustomFieldDefsResponse>(
    `/api/v1/integrations/${integrationId}/jtl/customfields`,
  )
  return resp.data
}

/**
 * Load the full bullet_mapping (all channels) for an integration.
 */
export async function getBulletMappings(
  integrationId: string,
): Promise<BulletMappingsResponse> {
  const resp = await apiClient.get<BulletMappingsResponse>(
    `/api/v1/integrations/${integrationId}/bullet-mapping`,
  )
  return resp.data
}

/**
 * Upsert the bullet_mapping for a single sales-channel.
 */
export async function saveBulletMapping(
  integrationId: string,
  sales_channel_id: string,
  mapping: BulletMapping,
): Promise<SaveBulletMappingResponse> {
  const resp = await apiClient.put<SaveBulletMappingResponse>(
    `/api/v1/integrations/${integrationId}/bullet-mapping`,
    { sales_channel_id, mapping },
  )
  return resp.data
}
